// Expired login -> "sign in again" instead of a reload (5.5.0, item f).
//
// One pending sign-in per window, shared by every request that got a 401.
// It is satisfied either by the user signing in again in this window's box
// (ReloginBox) or by ANY other InsureDocs window writing a new token to the
// shared same-origin localStorage (the 'storage' event). Each waiting request
// is retried ONCE with the new token. The server refuses an expired token in
// the auth dependency before any handler runs, so a retried write can't be
// applied twice. The token never goes into a URL; the password only goes to
// POST /api/auth/token as a form body, exactly like the login page.

export class AuthCancelled extends Error {
  constructor() {
    super('Login expired — not signed in again');
    this.name = 'AuthCancelled';
  }
}

const subscribers = new Set();     // ReloginBox instances: (state) => void
const renewedListeners = new Set(); // e.g. AuthContext.refreshUser
let state = null;                  // null (no box) | { error, busy }
let pending = null;                // { promise, resolve, reject, stale }
let expectedUser = readUser();     // the user this window is signed in as

function readUser() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || 'null');
    return u && u.username ? u.username : null;
  } catch (_e) {
    return null;
  }
}

function emit() { subscribers.forEach((fn) => fn(state)); }

export function subscribe(fn) {
  subscribers.add(fn);
  fn(state);
  return () => { subscribers.delete(fn); };
}

export function onRenewed(fn) {
  renewedListeners.add(fn);
  return () => { renewedListeners.delete(fn); };
}

export function expectedUsername() { return expectedUser || readUser(); }

function onStorage(e) {
  if (pending && e.key === 'token' && e.newValue && e.newValue !== pending.stale) settle(true, e.newValue);
}

function settle(ok, value) {
  const p = pending;
  pending = null;
  state = null;
  window.removeEventListener('storage', onStorage);
  emit();
  if (!p) return;
  if (ok) {
    expectedUser = readUser() || expectedUser;
    renewedListeners.forEach((fn) => { try { fn(value); } catch (_e) { /* listener errors don't block */ } });
    p.resolve(value);
  } else {
    p.reject(value);
  }
}

// Resolves with a new token; rejects with AuthCancelled if the user cancels.
// If another window already renewed the token since `staleToken` was sent,
// resolves at once without showing the box.
export function waitForReauth(staleToken) {
  const current = localStorage.getItem('token');
  if (!pending && current && current !== staleToken) return Promise.resolve(current);
  if (!pending) {
    expectedUser = expectedUser || readUser();
    let resolve;
    let reject;
    const promise = new Promise((a, b) => { resolve = a; reject = b; });
    pending = { promise, resolve, reject, stale: staleToken };
    window.addEventListener('storage', onStorage);
    state = { error: '', busy: false };
    emit();
  }
  return pending.promise;
}

export function cancelReauth() {
  if (pending) settle(false, new AuthCancelled());
}

export async function signInAgain(password, typedUsername) {
  if (!pending) return;
  const known = expectedUsername();
  const username = known || typedUsername || '';
  state = { error: '', busy: true };
  emit();
  const fail = (msg) => { state = { error: msg, busy: false }; emit(); };
  try {
    const form = new FormData();
    form.append('username', username);
    form.append('password', password);
    const tr = await fetch('/api/auth/token', { method: 'POST', body: form });
    if (!tr.ok) { fail(tr.status === 400 ? 'Incorrect password.' : `Sign-in failed (HTTP ${tr.status}).`); return; }
    const { access_token: token } = await tr.json();
    const me = await fetch('/api/auth/me', { headers: { Authorization: `Bearer ${token}` } });
    if (!me.ok) { fail('This account can’t sign in right now — ask an administrator.'); return; }
    const meData = await me.json();
    if (known && meData.username !== known) { fail(`Please sign in as ${known}.`); return; }
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(meData));
    settle(true, token);
  } catch (e) {
    fail('Sign-in failed: ' + (e && e.message ? e.message : e));
  }
}

// fetch() with the CURRENT token; on 401 wait for a new login, then re-send the
// same request once. A second 401 is returned to the caller as-is.
export async function authFetch(url, opts = {}) {
  const send = (tok) => fetch(url, { ...opts, headers: { ...(opts.headers || {}), Authorization: `Bearer ${tok}` } });
  const stale = localStorage.getItem('token');
  const res = await send(stale);
  if (res.status !== 401) return res;
  return send(await waitForReauth(stale));
}
